import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Users, UserCheck, UserPlus, ClipboardCheck, Calendar, Heart, AlertTriangle, ChevronRight, Check } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { toast } from 'sonner';

const typeBadge = (t) => {
  const map = { regular: 'border-blue-200 text-blue-700', visitor: 'border-amber-200 text-amber-700', occasional: 'border-teal-200 text-teal-700', inactive: 'border-slate-300 text-slate-500' };
  return map[t] || 'border-slate-200 text-slate-600';
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState([]);
  const [trendEvents, setTrendEvents] = useState([]);
  const [recs, setRecs] = useState([]);
  const [prayerList, setPrayerList] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = () => {
    Promise.all([
      api.get('/dashboard/stats'),
      api.get('/reports/attendance-trends?weeks=8'),
      api.get('/recommendations'),
      api.get('/prayer/today'),
    ]).then(([s, t, r, p]) => {
      setStats(s.data);
      const tData = t.data.trends || t.data;
      setTrends((Array.isArray(tData) ? tData : []).map(d => ({ ...d, date: new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) })));
      setTrendEvents(t.data.events || []);
      setRecs(r.data);
      setPrayerList(p.data.assignments || []);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleApprove = async (recId) => {
    await api.post(`/recommendations/${recId}/approve`);
    setRecs(prev => prev.filter(r => r.recommendation_id !== recId));
  };

  const handlePrayerComplete = async (personId) => {
    try {
      await api.post(`/prayer/complete/${personId}`);
      setPrayerList(prev => prev.filter(p => p.person_id !== personId));
      toast.success('Marked as prayed for');
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="h-8 w-8 border-3 border-blue-700 border-t-transparent rounded-full animate-spin" /></div>;

  const statCards = [
    { label: 'Total People', value: stats?.total_people, icon: Users, color: 'text-blue-700', bg: 'bg-blue-50' },
    { label: 'Regulars', value: stats?.regulars, icon: UserCheck, color: 'text-emerald-700', bg: 'bg-emerald-50' },
    { label: 'Visitors', value: stats?.visitors, icon: UserPlus, color: 'text-amber-700', bg: 'bg-amber-50' },
    { label: 'Last Attendance', value: stats?.latest_attendance, icon: ClipboardCheck, color: 'text-violet-700', bg: 'bg-violet-50' },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="relative rounded-xl overflow-hidden h-44 sm:h-52">
        <img src="https://customer-assets.emergentagent.com/job_pastoral-pulse/artifacts/u7afiud1_WalnutGroveMethodistChurch-0131.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/70 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-center px-6 sm:px-8">
          <p className="text-sm text-blue-300 font-medium mb-1">Welcome back,</p>
          <h2 data-testid="welcome-heading" className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] tracking-tight">{user?.name || 'Admin'}</h2>
          <p className="text-sm text-slate-300 mt-1">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>
      </div>

      {recs.length > 0 && user?.role === 'admin' && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-900">Visitor Recommendations</p>
                <p className="text-xs text-amber-700 mt-1">{recs.length} visitor(s) may qualify as regulars</p>
                <div className="mt-2 space-y-2">
                  {recs.map(r => (
                    <div key={r.recommendation_id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-amber-200">
                      <div>
                        <span className="text-sm font-medium text-slate-900">{r.person_name}</span>
                        <span className="text-xs text-slate-500 ml-2">{r.reason}</span>
                      </div>
                      <Button data-testid={`approve-rec-${r.recommendation_id}`} size="sm" variant="outline" className="text-xs h-7 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => handleApprove(r.recommendation_id)}>Approve</Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <Card key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`} className={`border-slate-200 shadow-sm animate-fade-in stagger-${i + 1}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{s.label}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1 font-['Outfit']">{s.value ?? '—'}</p>
                </div>
                <div className={`h-11 w-11 rounded-lg ${s.bg} flex items-center justify-center`}>
                  <s.icon className={`h-5 w-5 ${s.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Outfit']">Attendance Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64" data-testid="attendance-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                  {trendEvents.map((evt, i) => {
                    const colors = ['#1E40AF', '#DC2626', '#059669', '#7C3AED', '#D97706', '#0891B2'];
                    return <Bar key={evt.event_id} dataKey={`present_${evt.event_id}`} fill={colors[i % colors.length]} radius={[4, 4, 0, 0]} name={evt.name} />;
                  })}
                  <ReferenceLine y={12} stroke="#F97316" strokeWidth={2} strokeDasharray="6 3" label={{ value: 'Goal: 12', position: 'right', fill: '#F97316', fontSize: 12 }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2"><Heart className="h-4 w-4 text-blue-600" />Prayer Today</CardTitle>
                <Button variant="ghost" size="sm" className="text-xs text-blue-700 hover:text-blue-800" onClick={() => navigate('/prayer')}>View All <ChevronRight className="h-3 w-3 ml-1" /></Button>
              </div>
            </CardHeader>
            <CardContent data-testid="prayer-today-list">
              {prayerList.length === 0 && <p className="text-sm text-slate-500 flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" />All prayers completed!</p>}
              {prayerList.slice(0, 8).map(p => (
                <div key={p.person_id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                  <Checkbox
                    data-testid={`prayer-check-dash-${p.person_id}`}
                    onCheckedChange={() => handlePrayerComplete(p.person_id)}
                    className="border-slate-300 data-[state=checked]:bg-blue-700 data-[state=checked]:border-blue-700"
                  />
                  <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-800">{p.first_name[0]}{p.last_name[0]}</div>
                  <span className="text-sm text-slate-700">{p.first_name} {p.last_name}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2"><Calendar className="h-4 w-4 text-blue-600" />Birthdays</CardTitle>
              </div>
            </CardHeader>
            <CardContent data-testid="upcoming-birthdays">
              {stats?.upcoming_birthdays?.length === 0 && <p className="text-sm text-slate-500">No upcoming birthdays</p>}
              {stats?.upcoming_birthdays?.map(b => (
                <div key={b.person_id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <span className="text-sm text-slate-700">{b.first_name} {b.last_name}</span>
                  <Badge variant="outline" className="text-xs">{b.days_until === 0 ? 'Today!' : `${b.days_until}d`}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {stats?.absent_people?.length > 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />Absent 3+ Weeks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="absent-people-list">
              {stats.absent_people.slice(0, 9).map(a => (
                <div key={a.person_id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors" onClick={() => navigate(`/people/${a.person_id}`)}>
                  <span className="text-sm text-slate-700">{a.first_name} {a.last_name}</span>
                  <Badge variant="outline" className={`text-xs ${typeBadge(a.person_type)}`}>{a.person_type}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
